import {AlertTriangle} from "lucide-react";
import React, {useState} from "react";
import {ApiBankInfo, ApiBuildingInfo, ApiDeviceInfo, ApiFaultLog} from "./solutrakApiService";
import {TrafficRecord} from "./TrafficInformationView";


type FaultInformationViewProps = {
    selectedBuilding: ApiBuildingInfo | null;
    selectedBank: ApiBankInfo | null;
    selectedDevice: ApiDeviceInfo | null;
    isLoadingFaults: boolean;
    faultLogsData: ApiFaultLog[];
    faultsError: string | null;
    handleBankSelect: (bank: ApiBankInfo) => void;
    handleDeviceSelect: (device: ApiDeviceInfo) => void;
    elevatorTrafficData: TrafficRecord[];
    elevatorCapacity: number;
}

export default function FaultInformationView({selectedBuilding, selectedBank, selectedDevice, isLoadingFaults, faultLogsData, faultsError, handleBankSelect, handleDeviceSelect, elevatorTrafficData, elevatorCapacity}: FaultInformationViewProps) {
    const [filterDateFrom, setFilterDateFrom] = useState("2026-03-25T00:00");
    const [filterDateTo, setFilterDateTo] = useState("2026-03-25T23:59");
    const [filterElevator, setFilterElevator] = useState<string>("all");
    const [visibleFaults, setVisibleFaults] = useState(5);

    const ELEVATOR_CAPACITY = elevatorCapacity;

    const getFilteredTrafficData = () => {
        return elevatorTrafficData.filter((r) => {
            const dt = new Date(r.dateTime);
            const from = new Date(filterDateFrom);
            const to = new Date(filterDateTo);
            if (dt < from || dt > to) return false;
            if (filterElevator !== "all" && r.elevatorId !== filterElevator) return false;
            return true;
        });
    };

    const handleExport = () => {
        const filtered = getFilteredTrafficData();
        const faults: { severity: string; elevator: string; message: string; time: string }[] = [];

        filtered.forEach((r) => {
            if (r.peopleInElevator > ELEVATOR_CAPACITY) {
                faults.push({ severity: "CRITICAL", elevator: r.elevatorId, message: `Overcapacity: ${r.peopleInElevator} people (max ${ELEVATOR_CAPACITY})`, time: new Date(r.dateTime).toLocaleString() });
            } else if (r.peopleInElevator > ELEVATOR_CAPACITY * 0.9) {
                faults.push({ severity: "WARNING", elevator: r.elevatorId, message: `Near capacity: ${r.peopleInElevator}/${ELEVATOR_CAPACITY} people`, time: new Date(r.dateTime).toLocaleString() });
            }
        });

        const elevatorIds = filtered.map(r => r.elevatorId).filter((id, idx, arr) => arr.indexOf(id) === idx);
        elevatorIds.forEach((eid) => {
            const elData = filtered.filter(r => r.elevatorId === eid);
            const netFlow = elData.reduce((s, r) => s + r.peopleIn - r.peopleOut, 0);
            if (netFlow > 50) {
                faults.push({ severity: "WARNING", elevator: eid, message: `Persistent inflow imbalance: net +${netFlow} people`, time: "Aggregate" });
            }
            const totalActivity = elData.reduce((s, r) => s + r.peopleIn + r.peopleOut, 0);
            if (totalActivity === 0 && elData.length > 3) {
                faults.push({ severity: "CRITICAL", elevator: eid, message: "No passenger activity detected — possible out of service", time: "Aggregate" });
            }
            for (let i = 1; i < elData.length; i++) {
                const prev = elData[i - 1].peopleInElevator;
                const curr = elData[i].peopleInElevator;
                if (curr - prev > ELEVATOR_CAPACITY * 0.5 && prev < ELEVATOR_CAPACITY * 0.3) {
                    faults.push({ severity: "INFO", elevator: eid, message: `Sudden occupancy spike: ${prev} → ${curr}`, time: new Date(elData[i].dateTime).toLocaleString() });
                }
            }
        });

        const csvRows = ["Severity,Elevator,Message,Time"];
        faults.forEach((f) => {
            csvRows.push(`${f.severity},"${f.elevator}","${f.message}","${f.time}"`);
        });

        const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `fault-report-${filterDateFrom.slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <>
            {/* Date/Time Filter Bar & Export */}
            <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-4 mb-6">
                <div className="flex flex-wrap items-end gap-4">
                    <div className="flex-1 min-w-[180px]">
                        <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">From</label>
                        <input
                            type="datetime-local"
                            value={filterDateFrom}
                            onChange={(e) => setFilterDateFrom(e.target.value)}
                            className="w-full bg-slate-900/60 border border-slate-600/50 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50"
                        />
                    </div>
                    <div className="flex-1 min-w-[180px]">
                        <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">To</label>
                        <input
                            type="datetime-local"
                            value={filterDateTo}
                            onChange={(e) => setFilterDateTo(e.target.value)}
                            className="w-full bg-slate-900/60 border border-slate-600/50 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50"
                        />
                    </div>
                    <div className="flex-1 min-w-[160px]">
                        <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Elevator</label>
                        <select
                            value={filterElevator}
                            onChange={(e) => setFilterElevator(e.target.value)}
                            className="w-full bg-slate-900/60 border border-slate-600/50 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50"
                        >
                            <option value="all">All Elevators</option>
                            {elevatorTrafficData
                                .map(r => r.elevatorId)
                                .filter((id, idx, arr) => arr.indexOf(id) === idx)
                                .map((eid) => (
                                    <option key={eid} value={eid}>{eid}</option>
                                ))}
                        </select>
                    </div>
                    <button
                        onClick={handleExport}
                        className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white text-sm rounded-lg transition font-semibold"
                    >
                        📥 Export CSV
                    </button>
                </div>
            </div>

            {/* Fault & Anomaly Detection */}
            <div className="flex justify-between items-center mb-4">
                <div>
                    <h2 className="text-xl font-bold text-white mb-1">Fault & Anomaly Detection</h2>
                    <p className="text-sm text-slate-400">Automatically flagged issues from traffic patterns</p>
                </div>
                <span className="text-3xl">🔍</span>
            </div>
            {(() => {
                const filtered = getFilteredTrafficData();
                const faults: { severity: "critical" | "warning" | "info"; elevator: string; message: string; time: string }[] = [];

                // Detect overcrowding
                filtered.forEach((r) => {
                    if (r.peopleInElevator > ELEVATOR_CAPACITY) {
                        faults.push({ severity: "critical", elevator: r.elevatorId, message: `Overcapacity: ${r.peopleInElevator} people (max ${ELEVATOR_CAPACITY})`, time: new Date(r.dateTime).toLocaleString() });
                    } else if (r.peopleInElevator > ELEVATOR_CAPACITY * 0.9) {
                        faults.push({ severity: "warning", elevator: r.elevatorId, message: `Near capacity: ${r.peopleInElevator}/${ELEVATOR_CAPACITY} people`, time: new Date(r.dateTime).toLocaleString() });
                    }
                });

                // Detect imbalance
                const elevatorIds = filtered.map(r => r.elevatorId).filter((id, idx, arr) => arr.indexOf(id) === idx);
                elevatorIds.forEach((eid) => {
                    const elData = filtered.filter(r => r.elevatorId === eid);
                    const netFlow = elData.reduce((s, r) => s + r.peopleIn - r.peopleOut, 0);
                    if (netFlow > 50) {
                        faults.push({ severity: "warning", elevator: eid, message: `Persistent inflow imbalance: net +${netFlow} people (potential stuck/slow exit)`, time: "Aggregate" });
                    }
                    // Detect zero activity (possible breakdown)
                    const totalActivity = elData.reduce((s, r) => s + r.peopleIn + r.peopleOut, 0);
                    if (totalActivity === 0 && elData.length > 3) {
                        faults.push({ severity: "critical", elevator: eid, message: "No passenger activity detected — possible elevator out of service", time: "Aggregate" });
                    }
                    // Detect sudden spikes
                    for (let i = 1; i < elData.length; i++) {
                        const prev = elData[i - 1].peopleInElevator;
                        const curr = elData[i].peopleInElevator;
                        if (curr - prev > ELEVATOR_CAPACITY * 0.5 && prev < ELEVATOR_CAPACITY * 0.3) {
                            faults.push({ severity: "info", elevator: eid, message: `Sudden occupancy spike: ${prev} → ${curr}`, time: new Date(elData[i].dateTime).toLocaleString() });
                        }
                    }
                });

                // Sort: most recent first, then by severity within same time
                const severityOrder = { critical: 0, warning: 1, info: 2 } as const;
                faults.sort((a, b) => {
                    const timeA = a.time === "Aggregate" ? 0 : new Date(a.time).getTime();
                    const timeB = b.time === "Aggregate" ? 0 : new Date(b.time).getTime();
                    if (timeB !== timeA) return timeB - timeA;
                    return severityOrder[a.severity] - severityOrder[b.severity];
                });

                if (faults.length === 0) {
                    return (
                        <div className="text-center py-8">
                            <span className="text-4xl">✅</span>
                            <p className="text-lg font-semibold text-emerald-400 mt-3">No Issues Detected</p>
                            <p className="text-sm text-slate-400 mt-1">All elevators operating within normal parameters</p>
                        </div>
                    );
                }

                const visible = faults.slice(0, visibleFaults);

                return (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-800/80">
                                    <tr className="border-b border-slate-700/50">
                                        <th className="text-center py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Severity</th>
                                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Elevator</th>
                                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Issue</th>
                                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visible.map((fault, i) => {
                                        const severityConfig = {
                                            critical: { bg: "bg-red-500/5", badge: "bg-red-500/20 text-red-400", icon: "🚨" },
                                            warning: { bg: "", badge: "bg-yellow-500/20 text-yellow-400", icon: "⚠️" },
                                            info: { bg: "", badge: "bg-blue-500/20 text-blue-400", icon: "ℹ️" },
                                        };
                                        const cfg = severityConfig[fault.severity];
                                        return (
                                            <tr key={i} className={`border-b border-slate-700/30 hover:bg-slate-700/20 transition ${cfg.bg}`}>
                                                <td className="py-3 px-4 text-center">
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase ${cfg.badge}`}>
                                                        {cfg.icon} {fault.severity}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 text-cyan-300 font-semibold">{fault.elevator}</td>
                                                <td className="py-3 px-4 text-white">{fault.message}</td>
                                                <td className="py-3 px-4 text-slate-400 text-xs">{fault.time}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {faults.length > visibleFaults && (
                            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700/50">
                                <span className="text-sm text-slate-400">Showing {Math.min(visibleFaults, faults.length)} of {faults.length} detected issues</span>
                                <button
                                    onClick={() => setVisibleFaults((prev) => Math.min(prev + 10, faults.length))}
                                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg transition font-semibold"
                                >
                                    Show More
                                </button>
                            </div>
                        )}
                    </>
                );
            })()}
        </>
    );
}
